"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import CdnImage from "@/components/CdnImage";
import Link from "next/link";
import { useT } from "@/hooks/useT";
import { FiClock, FiDollarSign, FiLock } from "react-icons/fi";
import { trackRecommendation, setNavigationSource } from "@/lib/recommendation-tracker";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import dynamic from "next/dynamic";
// Lazy: TradingModal pulls the Polymarket CLOB SDK + builder relayer
// (~200-300KB). Cards on /markets/* render hundreds at a time but the
// modal only renders when the user actually clicks Trade.
const TradingModal = dynamic(() => import("./TradingModal"), { ssr: false });
import { useMarketData, MarketNotFoundError } from "@/hooks/useMarketData";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import { marketPageUrl, sourceUrl } from "@/lib/source-url";
import { withReferralParams } from "@/hooks/useReferralAttribution";

// Fallback data from link_preview stored in posts
// Note: Some posts have 'title', others have 'question' - accept both
interface FallbackData {
  question?: string;
  title?: string;  // Alternative field name used in some posts
  image?: string;
  outcomes?: string[];
  outcomePrices?: (number | string)[];
  url?: string;
  active?: boolean;
  closed?: boolean;
  endDate?: string | null;
  /** Parent event slug — when present, YES/NO clicks navigate to the event
   * page with ?market+outcome params so the sidebar Place Trade picks the
   * sub-market + side, instead of opening the bottom-sheet modal. Matches
   * the flow of tapping a market row inside the outcomes table. */
  eventSlug?: string;
  /** Source override stored alongside the link preview when the post was
   * created (e.g. trade-share posts persist `source: "hyperliquid"`).
   * Authoritative — wins over the slug-shape heuristic so HL markets
   * don't get fetched against Polymarket's API and rendered as
   * "Market archived". */
  source?: "polymarket" | "kalshi" | "predict" | "hyperliquid";
}

interface PolymarketMarketCardProps {
  slug: string;
  postId?: string;
  postAuthorId?: string;
  fallbackData?: FallbackData;
  source?: "polymarket" | "kalshi" | "predict" | "hyperliquid";
}

export default function PolymarketMarketCard({
  slug,
  postId,
  postAuthorId,
  fallbackData,
  source: sourceProp,
}: PolymarketMarketCardProps) {
  const t = useT();
  // Skip the network fetch entirely if:
  // 1. fallbackData already has everything we need to render, AND
  // 2. the market is archived (closed or end date in the past) — live data is pointless.
  // Prevents 404 console noise for old posts referencing resolved markets.
  const hasCompleteFallback = !!(
    fallbackData &&
    (fallbackData.question || fallbackData.title) &&
    fallbackData.outcomes?.length &&
    fallbackData.outcomePrices?.length
  );
  const isArchivedByFallback = !!(
    fallbackData?.closed === true ||
    fallbackData?.active === false ||
    (fallbackData?.endDate && new Date(fallbackData.endDate).getTime() < Date.now())
  );
  const skipFetch = hasCompleteFallback && isArchivedByFallback;

  // Source resolution priority:
  //  1. explicit prop from the caller
  //  2. fallbackData.source — persisted on link_preview when the post was
  //     created from a non-Polymarket trade share
  //  3. slug-shape heuristic (kx... = Kalshi, all-digits = Predict)
  //  4. default Polymarket
  // Without (2) HL trade-share posts hit /api/polymarket/market/<hl-slug>,
  // 404, and render as "Market archived" with the wrong source logo.
  const detectedSource = sourceProp
    || fallbackData?.source
    || (slug.toLowerCase().startsWith('kx')
      ? 'kalshi'
      : /^\d+$/.test(slug)
        ? 'predict'
        // Hyperliquid Recurring outcomes encode the question + outcome
        // index into the slug suffix: "...-q-2-o1-yes". Detect it so
        // legacy MarketCard call sites (ActivityFeed, recommendations)
        // route correctly even when the caller hasn't been updated to
        // forward `source` explicitly.
        : /-q-\d+-o\d+-(yes|no)$/i.test(slug)
          ? 'hyperliquid'
          : 'polymarket');
  const isKalshi = detectedSource === 'kalshi';
  const isPredict = detectedSource === 'predict';
  const isHyperliquid = detectedSource === 'hyperliquid';

  // TanStack Query: auto-deduplicates requests for same slug across all components
  const { data: marketData, isLoading: loading, error, isError } = useMarketData(slug, {
    enabled: !skipFetch,
    source: detectedSource,
  });
  const isMarketNotFound = isError && error instanceof MarketNotFoundError;

  const [tradingModal, setTradingModal] = useState<{
    isOpen: boolean;
    outcome: "YES" | "NO";
    actualOutcome?: string;
    altActualOutcome?: string;
    outcomes?: string[];
  }>({ isOpen: false, outcome: "YES" });
  const [showClosedMessage, setShowClosedMessage] = useState(false);
  const router = useRouter();

  const { openKalshi, openPredict } = useTradingModalStore();

  // Loading state
  if (loading) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200/80 dark:border-white/[0.04] shadow-sm overflow-hidden animate-pulse">
        <div className="flex gap-3 p-3">
          <div className="flex-1 min-w-0">
            <div className="flex gap-2.5">
              <div className="w-10 h-10 bg-gray-200 dark:bg-[#0f0f12] rounded-[5px] flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-3.5 bg-gray-200 dark:bg-[#0f0f12] rounded w-3/4" />
                <div className="h-3 bg-gray-200 dark:bg-[#0f0f12] rounded w-1/2" />
              </div>
            </div>
          </div>
          <div className="flex gap-1.5 flex-shrink-0">
            <div className="h-7 w-16 bg-gray-100 dark:bg-[#0f0f12]/50 rounded-[5px]" />
            <div className="h-7 w-16 bg-gray-100 dark:bg-[#0f0f12]/50 rounded-[5px]" />
          </div>
        </div>
      </div>
    );
  }

  // Error state - show fallback data if available, otherwise error
  if (isError || (!loading && !marketData)) {
    // Show "Archived" fallback for:
    //   - 404 (market truly archived), or
    //   - skipFetch (pre-determined archived), or
    //   - ANY transient error when fallbackData has a title — the activity
    //     feed proxies upstream 5xx/timeouts as errors, but the event row
    //     already ships title+image inline. Rendering a soft "Market Closed"
    //     placeholder reads cleaner than a full red banner that disappears
    //     on refresh anyway.
    const fallbackTitle = fallbackData?.question || fallbackData?.title;
    const showArchivedFallback = (isMarketNotFound || skipFetch || isError) && fallbackTitle;
    // Transient (non-404) error: the market is alive, the proxy just hiccupped.
    // Clicking the soft fallback should navigate to the market page (so the
    // user can retry there) instead of popping a misleading "Market Closed"
    // overlay reserved for genuinely-archived rows.
    const isTransientError = isError && !isMarketNotFound && !skipFetch;
    if (showArchivedFallback) {
      return (
        <div
          className="group relative bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200/80 dark:border-white/[0.04] shadow-sm overflow-hidden opacity-75 cursor-pointer"
          onClick={(e) => {
            if (isTransientError) {
              // Let the upstream <Link> wrapper (when present) handle nav.
              // If wrapped in a button-only context, fall through silently.
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            setShowClosedMessage(true);
            setTimeout(() => setShowClosedMessage(false), 2000);
          }}
        >
          {/* Animated "Market Closed" overlay — only for genuinely archived
              rows, never for transient backend errors (the market may be
              perfectly alive; the proxy just blipped). */}
          <AnimatePresence>
            {!isTransientError && showClosedMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -10 }}
                transition={{ type: "spring", duration: 0.4, bounce: 0.3 }}
                className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-[5px] z-10"
              >
                <motion.div
                  initial={{ rotate: -5 }}
                  animate={{ rotate: [0, -3, 3, -2, 2, 0] }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="flex items-center gap-2 bg-red-500 text-white px-4 py-2.5 rounded-[5px] shadow-lg"
                >
                  <FiLock className="w-4 h-4" />
                  <span className="font-sans font-bold text-sm">{t("marketCard.closedHeyFren")}</span>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="p-3">
            <div className="flex flex-col sm:flex-row sm:gap-3">
              {/* Market info */}
              <div className="flex gap-2.5 flex-1 min-w-0">
                {fallbackData.image && (
                  <div className="relative w-12 h-12 flex-shrink-0 rounded-[5px] overflow-hidden bg-gray-50 dark:bg-[#0f0f12]">
                    <CdnImage src={fallbackData.image} alt={fallbackTitle} fill sizes="48px" className="object-cover grayscale" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs font-sans font-semibold text-gray-500 dark:text-gray-400 mb-1 line-clamp-2 leading-snug">
                    {fallbackTitle}
                  </h3>
                  <div className="flex items-center gap-2 text-[10px] font-sans text-gray-500 dark:text-gray-400">
                    <img
                      src={
                        isHyperliquid ? '/logos/hyperliquid/symbol-mint.svg'
                        : isPredict ? '/logos/predictfun/Predict-Icon-PB.png'
                        : isKalshi ? '/logos/kalshi/k-icon.svg'
                        : '/logos/polymarket/icon-blue.svg'
                      }
                      alt={
                        isHyperliquid ? 'Hyperliquid'
                        : isPredict ? 'Predict.fun'
                        : isKalshi ? 'Kalshi'
                        : 'Polymarket'
                      }
                      className={`w-3.5 h-3.5 rounded-sm ${isKalshi ? 'bg-[#09C285] p-[2px]' : ''}`}
                    />
                    <span className="px-1.5 py-0.5 rounded-[5px] font-medium bg-gray-500/10 text-gray-600 dark:text-gray-400">
                      {t("marketCard.archived")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Static prices — below on mobile, side on sm+ */}
              {fallbackData.outcomes && fallbackData.outcomePrices && (
                <div className="flex sm:flex-col gap-1.5 mt-2.5 sm:mt-0 sm:flex-shrink-0">
                  <div className="rounded-[5px] flex-1 sm:flex-none sm:w-[90px] px-3 sm:px-2 py-2 sm:py-1.5 bg-gray-50 dark:bg-[#0f0f12]/20 border border-gray-200 dark:border-white/[0.04] opacity-60">
                    <div className="flex items-center justify-center sm:justify-start gap-1">
                      <span className="text-[8px] font-sans font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        {fallbackData.outcomes[0] || "YES"}
                      </span>
                      <span className="text-xs font-sans font-bold text-gray-900 dark:text-white">
                        {((Number(fallbackData.outcomePrices?.[0]) || 0.5) * 100).toFixed(1)}¢
                      </span>
                    </div>
                  </div>
                  <div className="rounded-[5px] flex-1 sm:flex-none sm:w-[90px] px-3 sm:px-2 py-2 sm:py-1.5 bg-gray-50 dark:bg-[#0f0f12]/20 border border-gray-200 dark:border-white/[0.04] opacity-60">
                    <div className="flex items-center justify-center sm:justify-start gap-1">
                      <span className="text-[8px] font-sans font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        {fallbackData.outcomes[1] || "NO"}
                      </span>
                      <span className="text-xs font-sans font-bold text-gray-900 dark:text-white">
                        {((Number(fallbackData.outcomePrices?.[1]) || 0.5) * 100).toFixed(1)}¢
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // No fallback data - show error
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-[5px] border border-red-200 dark:border-red-800/50 p-6">
        <p className="text-red-600 dark:text-red-400 text-sm font-sans">
          {error?.message || "Market not found"}
        </p>
      </div>
    );
  }

  // Guard for TypeScript — after early returns above, marketData is always defined here
  if (!marketData) return null;

  // Use cached prices from API (no individual polling to avoid 429 rate limiting)
  // Ensure values are numbers to prevent .toFixed() errors
  const yesPrice = Number(marketData.outcomePrices?.[0] || fallbackData?.outcomePrices?.[0] || 0.5) || 0.5;
  const noPrice = Number(marketData.outcomePrices?.[1] || fallbackData?.outcomePrices?.[1] || 0.5) || 0.5;
  const yesPercent = (yesPrice * 100).toFixed(1);
  const noPercent = (noPrice * 100).toFixed(1);

  // Use real outcome names from market data
  const outcome1Label = (marketData.outcomes?.[0] || fallbackData?.outcomes?.[0] || "Yes").toUpperCase();
  const outcome2Label = (marketData.outcomes?.[1] || fallbackData?.outcomes?.[1] || "No").toUpperCase();

  // Format volume - ensure it's a number first
  const formatVolume = (vol: number | string | null | undefined) => {
    const volume = Number(vol) || 0;
    if (volume >= 1_000_000) {
      return `$${(volume / 1_000_000).toFixed(1)}M`;
    } else if (volume >= 1_000) {
      return `$${(volume / 1_000).toFixed(1)}K`;
    }
    return `$${volume.toFixed(0)}`;
  };

  // Format date
  const formatEndDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Use API fields as source of truth for market status.
  // Do NOT use endDate — Polymarket often leaves endDate stale while the market remains open.
  const isActive = marketData.active && marketData.acceptingOrders && !marketData.closed;

  // Handle click on closed market
  const handleClosedClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (showClosedMessage) return; // Prevent spam clicks
    setShowClosedMessage(true);
    setTimeout(() => setShowClosedMessage(false), 2000);
  };

  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="group relative bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200/80 dark:border-white/[0.04] shadow-sm transition-colors duration-200 overflow-hidden"
    >

      <div className="relative p-3">
        {/* Layout: stacked on mobile, horizontal on sm+ */}
        <div className="flex flex-col sm:flex-row sm:gap-3">
          {/* Market info */}
          <div className="flex gap-2.5 flex-1 min-w-0">
            {(marketData.image || fallbackData?.image) && (
              <div className="relative w-12 h-12 flex-shrink-0 rounded-[5px] overflow-hidden bg-gray-50 dark:bg-[#0f0f12]">
                <CdnImage src={marketData.image || fallbackData?.image || ''} alt={marketData.question || ''} fill sizes="48px" className="object-cover" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              {isActive ? (
                <Link
                  href={
                    isHyperliquid
                      ? `/hyperliquid/event/${slug}`
                      : isKalshi
                        ? `${sourceUrl(`/market/${slug}`, 'kalshi')}`
                        : withReferralParams(
                            marketPageUrl({ marketSlug: slug, eventSlug: (marketData as any).parentEventSlug, source: 'polymarket', eventCategory: (marketData as any).eventCategory }),
                            postId,
                            postAuthorId,
                          )
                  }
                  prefetch={false}
                  onClick={(e) => { e.stopPropagation(); trackRecommendation(slug, 'view_detail', 'feed'); setNavigationSource('feed'); }}
                >
                  <h3 className="text-xs font-sans font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2 leading-snug hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    {marketData.question || fallbackData?.title || fallbackData?.question}
                  </h3>
                </Link>
              ) : (
                <h3
                  className="text-xs font-sans font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2 leading-snug cursor-pointer hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={handleClosedClick}
                >
                  {marketData.question || fallbackData?.title || fallbackData?.question}
                </h3>
              )}

              <div className="flex items-center gap-1.5 text-[10px] font-sans text-gray-500 dark:text-gray-400">
                <img
                  src={
                    isHyperliquid ? '/logos/hyperliquid/symbol-mint.svg'
                    : isPredict ? '/logos/predictfun/Predict-Icon-PB.png'
                    : isKalshi ? '/logos/kalshi/kalshi-logo.svg'
                    : '/logos/polymarket/icon-blue.svg'
                  }
                  alt={
                    isHyperliquid ? 'Hyperliquid'
                    : isPredict ? 'Predict.fun'
                    : isKalshi ? 'Kalshi'
                    : 'Polymarket'
                  }
                  className={`w-3.5 h-3.5 rounded-sm ${isKalshi ? 'bg-gray-900 dark:bg-white p-[1px]' : ''}`}
                />
                <span className="flex items-center gap-0.5">
                  <FiDollarSign className="w-2.5 h-2.5" />
                  {formatVolume(marketData.volume)}
                </span>
                {marketData.endDate && (
                  <>
                    <span className="text-gray-300 dark:text-white/[0.06]">·</span>
                    <span className="flex items-center gap-0.5">
                      <FiClock className="w-2.5 h-2.5" />
                      {formatEndDate(marketData.endDate)}
                    </span>
                  </>
                )}
                <span className="text-gray-300 dark:text-white/[0.06]">·</span>
                <span className={`px-1 py-0.5 rounded font-medium ${
                  marketData.closed
                    ? "bg-red-500/10 text-red-500"
                    : isActive
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "bg-gray-500/10 text-gray-600 dark:text-gray-400"
                }`}>
                  {marketData.closed ? "Closed" : isActive ? "Live" : "Inactive"}
                </span>
              </div>
            </div>
          </div>

          {/* YES/NO buttons — full width row below on mobile, side column on sm+ */}
          <div className="flex sm:flex-col gap-1.5 mt-2.5 sm:mt-0 sm:flex-shrink-0">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isActive) {
                // Hyperliquid: the slug IS the event slug, and
                // /hyperliquid/event/<slug> renders the YES + NO sides
                // together via the shared EventClient. No outcome param,
                // no modal — same UX as the portfolio "Sell" button.
                if (isHyperliquid) {
                  router.push(`/hyperliquid/event/${slug}`);
                  return;
                }
                // Polymarket + has parent event → navigate to the event page
                // with ?market+outcome so the sidebar Place Trade picks this
                // sub-market + YES. Same flow as clicking a row in the
                // outcomes grid — avoids stacking a modal on top of an open
                // event page.
                const parentEventSlug = (marketData as any)?.parentEventSlug || fallbackData?.eventSlug;
                if (!isKalshi && !isPredict && parentEventSlug) {
                  // Append `?via=&p=` when this card is rendered inside a
                  // user-authored post so the trade on the event page
                  // credits back to the post author. No-op when the props
                  // are absent (system feeds).
                  const baseUrl = `/polymarket/event/${parentEventSlug}?market=${slug}&outcome=yes`;
                  router.push(withReferralParams(baseUrl, postId, postAuthorId));
                  return;
                }
                // Real outcome labels for the leading (idx=0) side. Pass
                // them through to every adapter so the modal renders the
                // API labels (Over/Under, Up/Down, team names) instead of
                // falling back to "Yes"/"No" inside the modal.
                const outcomePair = Array.isArray(marketData?.outcomes) && marketData.outcomes.length >= 2
                  ? marketData.outcomes.slice(0, 2)
                  : undefined;
                const sideLabel = outcomePair?.[0] || marketData?.outcomes?.[0];
                const altSideLabel = outcomePair?.[1] || marketData?.outcomes?.[1];
                if (isKalshi) {
                  openKalshi({
                    marketTicker: slug,
                    marketTitle: marketData?.question || fallbackData?.question || fallbackData?.title || '',
                    outcome: "yes",
                    currentPrice: marketData?.outcomePrices?.[0] ?? 0.5,
                    altPrice: marketData?.outcomePrices?.[1] ?? 0.5,
                  });
                } else if (isPredict) {
                  openPredict({
                    marketId: slug,
                    marketSlug: slug,
                    outcome: "YES",
                    outcomePrice: marketData?.outcomePrices?.[0] ?? 0.5,
                    marketQuestion: marketData?.question || fallbackData?.question || fallbackData?.title || '',
                    marketImage: marketData?.image || fallbackData?.image,
                    altOutcomePrice: marketData?.outcomePrices?.[1] ?? 0.5,
                    outcomes: outcomePair,
                    actualOutcome: sideLabel,
                    altActualOutcome: altSideLabel,
                  });
                } else {
                  setTradingModal({
                    isOpen: true,
                    outcome: "YES",
                    actualOutcome: sideLabel,
                    altActualOutcome: altSideLabel,
                    outcomes: outcomePair,
                  });
                }
              } else {
                handleClosedClick(e);
              }
            }}
            className={`relative group/btn overflow-hidden rounded-[5px] flex-1 sm:flex-none sm:w-[130px] px-3 sm:px-2.5 py-2 sm:py-1.5 transition-colors ${
              isActive
                ? "bg-gray-50/50 dark:bg-[#0f0f12]/30 border border-emerald-500/30 hover:border-emerald-500 cursor-pointer"
                : "bg-gray-50 dark:bg-[#0f0f12]/20 border border-gray-200 dark:border-white/[0.04] cursor-pointer opacity-60"
            }`}
          >
            <div className="absolute inset-0 bg-emerald-500 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
            {/* justify-between (sm+) keeps label on the left, price pinned
                right with flex-shrink-0 + nowrap. With long player names
                ("ZSOMBOR PIROS", "TEREZA VALENTOVA") the label wraps to
                two lines instead of pushing the price past the button's
                overflow-hidden edge — that was the "47.5¢ → 47.5(" cut
                we were seeing in production. Width bumped 90→130 so
                two-line names breathe; there's plenty of card room. */}
            <div className="relative flex items-center justify-center sm:justify-between gap-1.5">
              <span className="text-[8px] font-sans font-semibold text-gray-500 dark:text-gray-400 group-hover/btn:text-white transition-colors uppercase leading-tight min-w-0">
                {outcome1Label}
              </span>
              <span className="text-xs font-sans font-bold text-gray-900 dark:text-white group-hover/btn:text-white transition-colors flex-shrink-0 whitespace-nowrap tabular-nums">
                {yesPercent}¢
              </span>
            </div>
          </button>

          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isActive) {
                // Hyperliquid: same as the YES button — both sides land
                // on the same event page; the page renders YES + NO
                // panels side-by-side and the user picks from there.
                if (isHyperliquid) {
                  router.push(`/hyperliquid/event/${slug}`);
                  return;
                }
                const parentEventSlug = (marketData as any)?.parentEventSlug || fallbackData?.eventSlug;
                if (!isKalshi && !isPredict && parentEventSlug) {
                  const baseUrl = `/polymarket/event/${parentEventSlug}?market=${slug}&outcome=no`;
                  router.push(withReferralParams(baseUrl, postId, postAuthorId));
                  return;
                }
                const outcomePair = Array.isArray(marketData?.outcomes) && marketData.outcomes.length >= 2
                  ? marketData.outcomes.slice(0, 2)
                  : undefined;
                const sideLabel = outcomePair?.[1] || marketData?.outcomes?.[1];
                const altSideLabel = outcomePair?.[0] || marketData?.outcomes?.[0];
                if (isKalshi) {
                  openKalshi({
                    marketTicker: slug,
                    marketTitle: marketData?.question || fallbackData?.question || fallbackData?.title || '',
                    outcome: "no",
                    currentPrice: marketData?.outcomePrices?.[1] ?? 0.5,
                    altPrice: marketData?.outcomePrices?.[0] ?? 0.5,
                  });
                } else if (isPredict) {
                  openPredict({
                    marketId: slug,
                    marketSlug: slug,
                    outcome: "NO",
                    outcomePrice: marketData?.outcomePrices?.[1] ?? 0.5,
                    marketQuestion: marketData?.question || fallbackData?.question || fallbackData?.title || '',
                    marketImage: marketData?.image || fallbackData?.image,
                    altOutcomePrice: marketData?.outcomePrices?.[0] ?? 0.5,
                    outcomes: outcomePair,
                    actualOutcome: sideLabel,
                    altActualOutcome: altSideLabel,
                  });
                } else {
                  setTradingModal({
                    isOpen: true,
                    outcome: "NO",
                    actualOutcome: sideLabel,
                    altActualOutcome: altSideLabel,
                    outcomes: outcomePair,
                  });
                }
              } else {
                handleClosedClick(e);
              }
            }}
            className={`relative group/btn overflow-hidden rounded-[5px] flex-1 sm:flex-none sm:w-[130px] px-3 sm:px-2.5 py-2 sm:py-1.5 transition-colors ${
              isActive
                ? "bg-gray-50/50 dark:bg-[#0f0f12]/30 border border-red-500/30 hover:border-red-500 cursor-pointer"
                : "bg-gray-50 dark:bg-[#0f0f12]/20 border border-gray-200 dark:border-white/[0.04] cursor-pointer opacity-60"
            }`}
          >
            <div className="absolute inset-0 bg-red-500 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
            <div className="relative flex items-center justify-center sm:justify-between gap-1.5">
              <span className="text-[8px] font-sans font-semibold text-gray-500 dark:text-gray-400 group-hover/btn:text-white transition-colors uppercase leading-tight min-w-0">
                {outcome2Label}
              </span>
              <span className="text-xs font-sans font-bold text-gray-900 dark:text-white group-hover/btn:text-white transition-colors flex-shrink-0 whitespace-nowrap tabular-nums">
                {noPercent}¢
              </span>
            </div>
          </button>
        </div>
        </div>

        {/* Animated "Market Closed" message */}
        <AnimatePresence>
          {showClosedMessage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -10 }}
              transition={{ type: "spring", duration: 0.4, bounce: 0.3 }}
              className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-[5px] z-10"
            >
              <motion.div
                initial={{ rotate: -5 }}
                animate={{ rotate: [0, -3, 3, -2, 2, 0] }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-center gap-2 bg-red-500 text-white px-4 py-2.5 rounded-[5px] shadow-lg"
              >
                <FiLock className="w-4 h-4" />
                <span className="font-sans font-bold text-sm">{t("marketCard.closedHeyFren")}</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {!isKalshi && (
        <TradingModal
          isOpen={tradingModal.isOpen}
          onClose={() => setTradingModal({ ...tradingModal, isOpen: false })}
          marketSlug={marketData.slug}
          outcome={tradingModal.outcome}
          actualOutcome={tradingModal.actualOutcome}
          altActualOutcome={tradingModal.altActualOutcome}
          outcomePrice={tradingModal.outcome === "YES" ? yesPrice : noPrice}
          altOutcomePrice={tradingModal.outcome === "YES" ? noPrice : yesPrice}
          outcomes={tradingModal.outcomes ?? marketData.outcomes}
          marketQuestion={marketData.question}
          marketImage={marketData.image}
          referralPostId={postId}
          referralAuthorId={postAuthorId}
        />
      )}
    </div>
  );
}
